import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';

extendZodWithOpenApi(z);

/** Presets for AI preferences */
export const aiPreferencesPresetEnums = ['exact', 'efficient', 'concise'] as const;
export const AiPreferencesPresetEnumsSchema = z.enum(aiPreferencesPresetEnums);
export type AiPreferencesPresetEnums = z.infer<typeof AiPreferencesPresetEnumsSchema>;

/**
 * Supported languages for AI-generated notes
 * This is the single source of truth for language configuration across the application
 */
export const aiNoteLanguageEnums = [
  'Arabic',
  'Danish',
  'Dutch',
  'English',
  'French',
  'German',
  'Italian',
  'Japanese',
  'Norwegian',
  'Portuguese',
  'Spanish',
  'Swedish',
] as const;
export const AiNoteLanguageSchema = z.enum(aiNoteLanguageEnums).openapi({
  description: 'Language for AI-generated notes',
  example: 'English',
});
export type AiNoteLanguage = z.infer<typeof AiNoteLanguageSchema>;

/**
 * Default language for AI-generated notes
 */
export const AI_NOTE_LANGUAGE_DEFAULT: AiNoteLanguage = 'English';

/**
 * Language options for dropdown menus
 * Provides label/value pairs for UI components
 */
export const aiNoteLanguageOptions = aiNoteLanguageEnums.map(lang => ({
  label: lang,
  value: lang,
})) as { label: AiNoteLanguage; value: AiNoteLanguage }[];

/**
 * Timesheet Assistant preferences schema (extracted for reuse across workspace/global contexts)
 */
export const TimesheetAssistantSchema = z.object({
  interval_minutes: z.number().int().min(5).max(1440).default(15).openapi({
    description: 'Interval in minutes for timesheet generation chunking (5 minutes to 24 hours)',
    example: 15,
  }),
  provider: z.enum(['Anthropic', 'OpenAI']).default('OpenAI').openapi({
    description: 'AI provider to use for timesheet generation',
    example: 'OpenAI',
  }),
  effort: z.enum(['none', 'low', 'medium']).default('low').openapi({
    description: 'Effort level for timesheet generation processing',
    example: 'low',
  }),
  note_language: AiNoteLanguageSchema.default(AI_NOTE_LANGUAGE_DEFAULT).openapi({
    description: 'Language for generated timesheet notes',
    example: 'English',
  }),
  daily_generation: z.enum(['weekdays_only', 'every_day', 'off']).default('weekdays_only').openapi({
    description: 'Frequency of daily email summaries',
    example: 'off',
  }),
  last_reset_at: z.string().datetime({ offset: true }).nullable().optional().openapi({
    description:
      'Timestamp of when AI memory was last reset. AI will ignore data before this time.',
    example: '2024-03-20T22:59:59.000Z',
  }),
});
export type TimesheetAssistant = z.infer<typeof TimesheetAssistantSchema>;

/** Valid field names for locking in workspace defaults (provider and effort are internal-only) */
export const timesheetAssistantLockableFields = [
  'interval_minutes',
  'note_language',
  'daily_generation',
] as const;
export type TimesheetAssistantLockableField = (typeof timesheetAssistantLockableFields)[number];

/**
 * AI Preferences (global, per-user)
 */
export type AiPreferences = z.infer<typeof AiPreferencesSchema>;
export const AiPreferencesSchema = z.object({
  id: z.string().uuid().optional().nullable(),
  enabled: z.boolean().default(false).openapi({
    description:
      'Is AI enabled for this user? Default true is used to draw initial UI, but AI will NOT activate until user confirms.',
    example: true,
  }),
  show_timesheet_predictions: z.boolean().default(false).openapi({
    description: 'Show predictions on the timesheet itself?',
  }),
  show_timeline_predictions: z.boolean().default(true).openapi({
    description: 'Show predictions on the timeline?',
  }),
  timeline_layout: z.enum(['compact', 'wide']).default('compact').openapi({
    description: 'How predictions are presented on the timeline',
    example: 'compact',
  }),
  updated_at: z.string().datetime({ offset: true }).optional().nullable().openapi({
    description:
      'When were these preferences last updated? If null/undefined, it means the user has not set any preferences yet.',
    example: '2024-03-20T22:59:59.000Z',
  }),
  preset: z
    .enum(['exact', 'efficient', 'concise'])
    .default('efficient')
    .openapi({
      description: 'Preset for the AI prediction settings',
      example: 'efficient',
    })
    .nullable(),
  fill_gap: z.number().int().min(0).max(60).default(5).openapi({
    description: 'How many minutes of smoothing / filling of gaps should be done?',
    example: 5,
  }),
  min_duration: z.number().int().min(0).max(15).default(5).openapi({
    description: 'Minimum duration of a prediction in minutes',
    example: 5,
  }),
  train_tags: z.boolean().default(true).openapi({
    description: 'Should the AI predict tags/labels? Results in higher volume of predictions.',
  }),
  suggest_note: z.boolean().default(false).openapi({
    description: 'Should the AI predict notes? ',
  }),
  relaxed_tag_matching: z.boolean().default(false).openapi({
    description:
      'Should the client-side be more relaxed when matching tags to existing time entries?',
  }),
  relaxed_note_matching: z.boolean().default(false).openapi({
    description:
      'Should the client-side be more relaxed when matching notes to existing time entries?',
  }),
  timesheet_assistant: TimesheetAssistantSchema.default({
    interval_minutes: 15,
    provider: 'OpenAI',
    effort: 'low',
    note_language: 'English',
    last_reset_at: null,
  }).openapi({
    description: 'Timesheet assistant specific preferences',
  }),
});

/**
 * Workspace-level timesheet assistant preferences (per-user, per-account)
 */
export type AiWorkspacePreferences = z.infer<typeof AiWorkspacePreferencesSchema>;
export const AiWorkspacePreferencesSchema = z.object({
  id: z.string().uuid().optional().nullable(),
  timely_account_id: z.number().int(),
  timesheet_assistant: TimesheetAssistantSchema.default({
    interval_minutes: 15,
    provider: 'OpenAI',
    effort: 'low',
    note_language: 'English',
    last_reset_at: null,
  }),
  updated_at: z.string().datetime({ offset: true }).optional().nullable(),
});

/**
 * Resolved workspace preferences returned to the frontend.
 * Includes the resolved values and which fields are locked by admin.
 */
export type ResolvedWorkspacePreferences = z.infer<typeof ResolvedWorkspacePreferencesSchema>;
export const ResolvedWorkspacePreferencesSchema = z.object({
  timesheet_assistant: TimesheetAssistantSchema,
  locked_fields: z
    .array(z.string())
    .default([])
    .openapi({
      description:
        'Fields that are locked/enforced by workspace admin and cannot be changed by users',
      example: ['interval_minutes', 'note_language'],
    }),
});

/**
 * Workspace defaults set by admins (enforced/locked settings)
 */
export type AiWorkspaceDefaults = z.infer<typeof AiWorkspaceDefaultsSchema>;
export const AiWorkspaceDefaultsSchema = z.object({
  id: z.string().uuid().optional().nullable(),
  timely_account_id: z.number().int(),
  timesheet_assistant: TimesheetAssistantSchema.partial().default({}),
  locked_fields: z
    .array(z.string())
    .default([])
    .openapi({
      description: 'Field names that are enforced for all users in this workspace',
      example: ['interval_minutes', 'note_language'],
    }),
  updated_at: z.string().datetime({ offset: true }).optional().nullable(),
});
